import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fullArticle, articles } from '@/mocks/articles';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL) || 'https://allchinabuy.watch';

function markdownToHtml(md: string): string {
  let html = md;
  html = html.replace(/^### (.+)$/gm, '<h3 class="font-heading text-xl font-semibold text-foreground-950 mt-8 mb-3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">$1</h2>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground-950 font-semibold">$1</strong>');
  html = html.replace(/\n\n/g, '</p><p class="text-foreground-700 leading-relaxed mb-4">');
  html = '<p class="text-foreground-700 leading-relaxed mb-4">' + html + '</p>';
  html = html.replace(/<p class="text-foreground-700 leading-relaxed mb-4"><\/p>/g, '');
  return html;
}

function buildArticleJsonLd(article: typeof fullArticle | typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'title' in article ? article.title : '',
    description: 'excerpt' in article ? article.excerpt : '',
    image: 'imageUrl' in article ? article.imageUrl : '',
    datePublished: 'date' in article ? article.date : '',
    author: {
      '@type': 'Person',
      name: 'author' in article ? article.author : 'AllChinaBuy Editorial Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AllChinaBuy',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/guide/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top when slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Use the full article for the main guide, or find from articles list
  const article = slug === fullArticle.slug ? fullArticle : articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const isFullArticle = 'content' in article;
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const articleJsonLd = buildArticleJsonLd(article);

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="bg-background-100 pt-24 pb-12 md:pt-32 md:pb-16">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-300 whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/" className="hover:text-primary-600 transition-colors duration-300 whitespace-nowrap">Blog</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 line-clamp-1">{'title' in article ? article.title : ''}</span>
            </nav>

            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
              {'category' in article ? article.category : ''}
            </span>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              {'title' in article ? article.title : ''}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{'date' in article ? article.date : ''}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{'author' in article ? article.author : 'AllChinaBuy Editorial Team'}</span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto -mt-6">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={'imageUrl' in article ? article.imageUrl : articles[0].imageUrl}
                alt={'title' in article ? article.title : ''}
                className="w-full h-64 md:h-96 object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="bg-background-50 py-12 md:py-16">
          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
            {isFullArticle ? (
              <div
                className="prose-custom"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(article.content) }}
              />
            ) : (
              <div className="space-y-4">
                <p className="text-foreground-700 leading-relaxed">
                  {'excerpt' in article ? article.excerpt : ''}
                </p>
                <p className="text-foreground-500 text-sm">
                  Full article content is available on the main platform.
                </p>
              </div>
            )}

            {/* CTA within article */}
            <div className="mt-12 p-8 bg-accent-100/60 rounded-2xl border border-accent-200/60 text-center">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                Ready to Start Sourcing Smarter?
              </h3>
              <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
                Get access to the AllChinaBuySpreadsheet system and start finding winning products today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-foreground-950 text-background-50 rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  allchinabuy sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Buy It Now
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 border border-background-300 text-foreground-700 rounded-lg text-sm font-medium hover:border-foreground-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Back to Home
                </Link>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-16">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  Related Articles
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/guide/${ra.slug}`}
                      className="group p-5 bg-background-100 rounded-xl border border-background-200/60 hover:border-accent-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-2">
                        {ra.category}
                      </span>
                      <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-foreground-400 text-xs">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-600 text-sm font-medium transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to AllChinaBuy Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}